import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthCalendarItem, GrowthContentAsset, GrowthMarketingService } from './growth-marketing.service';
import {
  buildMonthGrid,
  formatMonthLabel,
  groupCalendarItemsByDay,
  monthRangeUtc,
  utcDayKey
} from './growth-content-calendar-month.util';
import { CONTENT_STATUS_I18N_KEYS, normalizeContentStatus } from './growth-status.util';
import { GrowthContentReviewTaskSurfaceComponent } from './workflow-surfaces/growth-content-review-task-surface.component';
import { WizardApiService } from '../wizards/wizard-api.service';

type ReviewFilter = 'notApproved' | 'approved' | 'all';
type ReviewViewMode = 'calendar' | 'list';

interface CalendarContentEntry {
  content: GrowthContentAsset;
  calendarItem: GrowthCalendarItem | null;
  dayKey: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-article-publishing-list',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    SelectModule,
    TagModule,
    DialogModule,
    AinDatePipe,
    RouterLink,
    GrowthContentReviewTaskSurfaceComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.articlePublishing.reviewList.title')"
      [description]="t('growth.articlePublishing.reviewList.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.marketing.reviewQueue.help.page.title'"
        [descriptionKey]="'growth.marketing.reviewQueue.help.page.description'"
        [bulletKeys]="[
          'growth.marketing.reviewQueue.help.page.bullets.approval',
          'growth.marketing.reviewQueue.help.page.bullets.rejectReason',
          'growth.marketing.reviewQueue.help.page.bullets.publish'
        ]" />

      <section class="card">
        <div class="toolbar">
          <a routerLink="/growth/marketing/content-assets/new" pButton [label]="t('growth.articlePublishing.reviewList.actions.newArticle')"></a>
          <button
            pButton
            type="button"
            severity="help"
            [label]="t('growth.articlePublishing.reviewList.actions.startPublishWizard')"
            [loading]="startingWizard()"
            [disabled]="startingWizard()"
            (click)="startPublishWizard()"></button>
          <a routerLink="/growth/marketing/ace-studio" pButton severity="secondary" [label]="t('growth.articlePublishing.actions.openAceStudio')"></a>
          <div class="view-toggle">
            <button pButton type="button" [severity]="viewMode() === 'calendar' ? 'primary' : 'secondary'" [label]="t('growth.marketing.calendar.view.calendar')" (click)="setViewMode('calendar')"></button>
            <button pButton type="button" [severity]="viewMode() === 'list' ? 'primary' : 'secondary'" [label]="t('growth.marketing.calendar.view.list')" (click)="setViewMode('list')"></button>
          </div>
          @if (viewMode() === 'calendar') {
            <button pButton type="button" icon="pi pi-chevron-left" severity="secondary" (click)="shiftMonth(-1)"></button>
            <button pButton type="button" severity="secondary" [label]="t('growth.marketing.calendar.today')" (click)="goToday()"></button>
            <button pButton type="button" icon="pi pi-chevron-right" severity="secondary" (click)="shiftMonth(1)"></button>
            <span class="month-label">{{ monthLabel() }}</span>
          } @else {
            <ain-form-field [label]="t('growth.articlePublishing.reviewList.filters.approvalStatus')">
              <p-select
                [options]="statusFilterOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="statusFilter"
                appendTo="body"
                (onChange)="applyFilter()" />
            </ain-form-field>
          }
          <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="load()"></button>
        </div>

        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (viewMode() === 'calendar') {
          @if (calendarEntries().length === 0) {
            <ain-state type="empty" [title]="t('growth.marketing.calendar.empty')" />
          } @else {
            <div class="month-grid">
              @for (weekday of weekdayLabels(); track weekday) {
                <div class="weekday-head">{{ weekday }}</div>
              }
              @for (day of monthDays(); track day.key) {
                <div class="day-cell" [class.day-cell--muted]="!day.inMonth" [class.day-cell--today]="day.isToday">
                  <div class="day-number">{{ day.date.getUTCDate() }}</div>
                  <div class="day-items">
                    @for (entry of entriesForDay(day.key); track entry.content.id) {
                      <article class="item-chip">
                        <div class="item-chip__head">
                          <span class="item-chip__title">{{ entry.content.title }}</span>
                          <p-tag [value]="contentStatusLabel(entry.content.status)" [severity]="contentStatusSeverity(entry.content.status)" />
                        </div>
                        <small class="item-chip__meta">{{ entry.content.contentType }}</small>
                        <div class="item-chip__actions">
                          @for (action of contentActions(entry.content); track action.key) {
                            @if (action.kind === 'review') {
                              <button pButton type="button" size="small" [label]="action.label" (click)="openReviewDialog(entry.content.id)"></button>
                            } @else if (action.kind === 'submit') {
                              <button pButton type="button" size="small" [label]="action.label" (click)="submitForReview(entry.content.id)"></button>
                            } @else if (action.kind === 'open') {
                              <a [routerLink]="['/growth/marketing/article-publishing', entry.content.id]" pButton size="small" severity="secondary" [label]="action.label"></a>
                            }
                          }
                        </div>
                      </article>
                    }
                  </div>
                </div>
              }
            </div>
          }
        } @else if (filteredItems().length === 0) {
          <ain-state type="empty" [title]="t('growth.marketing.reviewQueue.empty')" />
        } @else {
          <p-table [value]="filteredItems()" [rows]="20" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.marketing.reviewQueue.columns.title') }}</th>
                <th>{{ t('growth.marketing.reviewQueue.columns.status') }}</th>
                <th>{{ t('growth.articlePublishing.reviewList.columns.contentType') }}</th>
                <th>{{ t('growth.articlePublishing.reviewList.columns.createdAt') }}</th>
                <th>{{ t('growth.marketing.reviewQueue.columns.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.title }}</td>
                <td>{{ contentStatusLabel(row.status) }}</td>
                <td>{{ row.contentType }}</td>
                <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
                <td class="actions">
                  @if (canReview(row)) {
                    <button pButton size="small" [label]="t('growth.articlePublishing.reviewList.actions.openReview')" (click)="openReviewDialog(row.id)"></button>
                  }
                  <a pButton size="small" severity="secondary" [routerLink]="['/growth/marketing/article-publishing', row.id]" [label]="openActionLabel(row)"></a>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>
    </ain-page>

    <p-dialog
      [header]="t('growth.marketing.calendar.reviewDialog.title')"
      [visible]="reviewDialogVisible()"
      (visibleChange)="onReviewDialogVisibleChange($event)"
      [modal]="true"
      [style]="{ width: 'min(640px, 92vw)' }"
      appendTo="body">
      @if (reviewContentAssetId()) {
        <ain-growth-content-review-task-surface
          [standaloneContentAssetId]="reviewContentAssetId()"
          (reviewCompleted)="onReviewCompleted()" />
      }
    </p-dialog>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: end; }
    .view-toggle { display: flex; gap: 6px; }
    .month-label { font-weight: 600; min-width: 160px; align-self: center; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .month-grid {
      display: grid;
      grid-template-columns: repeat(7, minmax(0, 1fr));
      gap: 1px;
      background: #e5e7eb;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      overflow: hidden;
    }
    .weekday-head, .day-cell { background: #fff; padding: 8px; min-height: 48px; }
    .weekday-head { font-size: 12px; font-weight: 600; color: #64748b; text-align: center; background: #f8fafc; }
    .day-cell { display: grid; grid-template-rows: auto 1fr; gap: 6px; min-height: 120px; align-content: start; }
    .day-cell--muted { background: #fafafa; color: #94a3b8; }
    .day-cell--today { box-shadow: inset 0 0 0 2px #93c5fd; }
    .day-number { font-size: 12px; font-weight: 600; color: #334155; }
    .day-items { display: grid; gap: 6px; }
    .item-chip { border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px; display: grid; gap: 4px; background: #fff; }
    .item-chip__head { display: flex; gap: 4px; justify-content: space-between; align-items: flex-start; }
    .item-chip__title { font-size: 11px; font-weight: 600; line-height: 1.3; }
    .item-chip__meta { font-size: 10px; color: #64748b; }
    .item-chip__actions { display: flex; gap: 4px; flex-wrap: wrap; }
  `]
})
export class GrowthArticlePublishingListComponent implements OnInit {
  private readonly service = inject(GrowthMarketingService);
  private readonly i18n = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly wizardApi = inject(WizardApiService);

  readonly loading = signal(false);
  readonly startingWizard = signal(false);
  readonly items = signal<GrowthContentAsset[]>([]);
  readonly filteredItems = signal<GrowthContentAsset[]>([]);
  readonly calendarItems = signal<GrowthCalendarItem[]>([]);
  readonly viewMode = signal<ReviewViewMode>('calendar');
  readonly reviewDialogVisible = signal(false);
  readonly reviewContentAssetId = signal<string | null>(null);

  readonly visibleYear = signal(new Date().getUTCFullYear());
  readonly visibleMonth = signal(new Date().getUTCMonth());

  statusFilter: ReviewFilter = 'notApproved';

  readonly monthDays = computed(() => buildMonthGrid(this.visibleYear(), this.visibleMonth()));
  readonly calendarEntries = computed(() => this.buildCalendarEntries());
  readonly entriesByDay = computed(() => {
    const grouped = new Map<string, CalendarContentEntry[]>();
    for (const entry of this.calendarEntries()) {
      const bucket = grouped.get(entry.dayKey) ?? [];
      bucket.push(entry);
      grouped.set(entry.dayKey, bucket);
    }
    return grouped;
  });
  readonly monthLabel = computed(() => formatMonthLabel(
    this.visibleYear(),
    this.visibleMonth(),
    this.i18n.activeCulture()
  ));

  constructor() {
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const view = params.get('view');
      if (view === 'list' || view === 'calendar') {
        this.viewMode.set(view);
      }
    });
  }

  get statusFilterOptions(): Array<{ value: ReviewFilter; label: string }> {
    return [
      { value: 'notApproved', label: this.t('growth.articlePublishing.reviewList.filters.notApproved') },
      { value: 'approved', label: this.t('growth.articlePublishing.reviewList.filters.approved') },
      { value: 'all', label: this.t('growth.articlePublishing.reviewList.filters.all') }
    ];
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    const { fromUtc, toUtc } = monthRangeUtc(this.visibleYear(), this.visibleMonth());

    this.service.listContent(undefined, 'article').subscribe({
      next: items => {
        this.items.set(items);
        this.applyFilter();
        this.service.getCalendar(fromUtc, toUtc).subscribe({
          next: calendarRows => {
            this.calendarItems.set(calendarRows.filter(row => this.isArticleCalendarItem(row, items)));
            this.loading.set(false);
          },
          error: () => this.loading.set(false)
        });
      },
      error: () => this.loading.set(false)
    });
  }

  setViewMode(mode: ReviewViewMode): void {
    this.viewMode.set(mode);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: mode },
      queryParamsHandling: 'merge'
    });
  }

  shiftMonth(delta: number): void {
    const date = new Date(Date.UTC(this.visibleYear(), this.visibleMonth() + delta, 1));
    this.visibleYear.set(date.getUTCFullYear());
    this.visibleMonth.set(date.getUTCMonth());
    this.load();
  }

  goToday(): void {
    const now = new Date();
    this.visibleYear.set(now.getUTCFullYear());
    this.visibleMonth.set(now.getUTCMonth());
    this.load();
  }

  weekdayLabels(): string[] {
    const formatter = new Intl.DateTimeFormat(this.i18n.activeCulture(), { weekday: 'short', timeZone: 'UTC' });
    return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(Date.UTC(2024, 0, 7 + index))));
  }

  entriesForDay(dayKey: string): CalendarContentEntry[] {
    return this.entriesByDay().get(dayKey) ?? [];
  }

  applyFilter(): void {
    const rows = this.items().filter(item => {
      const status = normalizeContentStatus(item.status);
      if (this.statusFilter === 'all') {
        return true;
      }
      if (this.statusFilter === 'approved') {
        return status === 'Approved' || status === 'Scheduled' || status === 'Published';
      }
      return status !== 'Approved' && status !== 'Scheduled' && status !== 'Published';
    });
    this.filteredItems.set(rows);
  }

  contentActions(content: GrowthContentAsset): Array<{ key: string; kind: string; label: string }> {
    const status = normalizeContentStatus(content.status);
    const actions: Array<{ key: string; kind: string; label: string }> = [];

    if (status === 'Draft' || status === 'AiGenerated' || status === 'Rejected') {
      actions.push({ key: 'submit', kind: 'submit', label: this.t('growth.marketing.campaigns.actions.submitForReview') });
    }
    if (status === 'InReview') {
      actions.push({ key: 'review', kind: 'review', label: this.t('growth.articlePublishing.reviewList.actions.openReview') });
    }
    const openLabel = status === 'Published'
      ? this.t('growth.articlePublishing.reviewList.actions.improve')
      : this.t('growth.marketing.calendar.actions.openContent');
    actions.push({ key: 'open', kind: 'open', label: openLabel });
    return actions;
  }

  openActionLabel(content: GrowthContentAsset): string {
    return normalizeContentStatus(content.status) === 'Published'
      ? this.t('growth.articlePublishing.reviewList.actions.improve')
      : this.t('growth.marketing.calendar.actions.openContent');
  }

  canReview(content: GrowthContentAsset): boolean {
    return normalizeContentStatus(content.status) === 'InReview';
  }

  openReviewDialog(contentId: string): void {
    this.reviewContentAssetId.set(contentId);
    this.reviewDialogVisible.set(true);
  }

  onReviewDialogVisibleChange(visible: boolean): void {
    this.reviewDialogVisible.set(visible);
    if (!visible) {
      this.reviewContentAssetId.set(null);
    }
  }

  onReviewCompleted(): void {
    this.reviewDialogVisible.set(false);
    this.reviewContentAssetId.set(null);
    this.load();
  }

  submitForReview(contentId: string): void {
    this.service.submitContentForReview(contentId).subscribe({ next: () => this.load() });
  }

  contentStatusLabel(status: unknown): string {
    const normalized = normalizeContentStatus(status);
    return this.t(CONTENT_STATUS_I18N_KEYS[normalized]);
  }

  contentStatusSeverity(status: unknown): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | undefined {
    const normalized = normalizeContentStatus(status);
    switch (normalized) {
      case 'Published':
      case 'Approved':
        return 'success';
      case 'InReview':
      case 'Scheduled':
        return 'info';
      case 'Rejected':
        return 'danger';
      case 'Draft':
      case 'AiGenerated':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  private buildCalendarEntries(): CalendarContentEntry[] {
    const items = this.items();
    const calendarByContentId = new Map<string, GrowthCalendarItem>();
    for (const row of this.calendarItems()) {
      if (row.contentAssetId) {
        calendarByContentId.set(row.contentAssetId, row);
      }
    }

    const entries: CalendarContentEntry[] = [];
    const seen = new Set<string>();

    for (const content of items) {
      const calendarItem = calendarByContentId.get(content.id) ?? null;
      const dayKey = calendarItem
        ? utcDayKey(calendarItem.scheduledAtUtc)
        : utcDayKey(content.createdAtUtc);
      if (!dayKey) {
        continue;
      }
      entries.push({ content, calendarItem, dayKey });
      seen.add(content.id);
    }

    return entries.sort((left, right) => left.content.title.localeCompare(right.content.title));
  }

  private isArticleCalendarItem(row: GrowthCalendarItem, articles: GrowthContentAsset[]): boolean {
    if (row.contentType === 'article') {
      return true;
    }
    if (!row.contentAssetId) {
      return false;
    }
    return articles.some(item => item.id === row.contentAssetId);
  }

  async startPublishWizard(): Promise<void> {
    this.startingWizard.set(true);
    try {
      const run = await firstValueFrom(
        this.wizardApi.startRun({ definitionKey: 'growth.article.publish' })
      );
      await this.router.navigate(['/wizards/runs', run.id]);
    } finally {
      this.startingWizard.set(false);
    }
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
